import type { AliRtcAudioTrack, AliRtcAuthInfo } from 'aliyun-rtc-sdk';
import { API_ORIGIN, getRequestPlatform, WECHAT_MINIPROGRAM_PLATFORM } from '@/app/constants';
import type { LiveAvatar, LiveClaimRtcInfo, LiveExitReason, LiveReferenceImageInput, LiveScene } from '@/app/types';

export interface RemoteVideoBinding {
  audioTrack: AliRtcAudioTrack;
  uid: string;
  videoTrack: number;
}

// 只在排序远端画面优先级时需要几个稳定枚举值，避免在模块顶层求值 aliyun-rtc-sdk 触发 SSR window 错误。
const ALI_RTC_VIDEO_TRACK_CAMERA = 1;
const ALI_RTC_VIDEO_TRACK_SCREEN = 2;
const ALI_RTC_VIDEO_TRACK_BOTH = 3;

/**
 * 用户文本输入回执 payload（server→client，text_input_ack=14）。
 * accepted=true 表示 owner 已校验通过且成功投递 AI（SendText 成功），不代表 AI 已生成结果。
 * client_msg_id 用于把对应的 pending 用户气泡翻成 sent。
 */
export interface LiveTextInputAckPayload {
  client_msg_id?: string;
  accepted?: boolean;
}

/**
 * 更新直播配置请求 payload（client→server，update_live_config=16）。
 *
 * 服务端要求每次都提交完整的 scene 与非空 references。
 * live_mode 在 claim 之后不可变，刻意不在这个消息里。
 */
export interface LiveUpdateConfigPayload {
  client_msg_id: string;
  config:
    | { editing: LiveEditingConfig }
    | { avatar: { scene?: string; references: LiveAvatarReferenceInput[] } }
    | { clear: Record<string, never> }
    | { remove_latest_reference: Record<string, never> };
}

export type LiveEditingConfig = {
  scene: LiveScene;
  references: LiveReferenceImageInput[];
  /** 输入源第一帧 JPEG base64，不含 data URL 前缀。切输入源时带上。 */
  base_image?: string;
};

export type LiveAvatarReferenceInput =
  | { avatar_asset_id: string; description?: string }
  | { reference_image: { uri: string; description: string } };

/** 服务端错误信令 payload（server→client，error=15）。msg_id 用于将对应的 pending 用户消息收口为 failed。 */
export interface LiveErrorPayload {
  msg_id?: string;
  code?: string;
  message?: string;
}

export interface LiveConfigRateLimitAckPayload {
  client_msg_id?: string;
  retry_after_ms?: number;
  limit?: number;
  window_ms?: number;
}

/**
 * 更新直播配置回执（server→client，update_live_config_ack=18）。
 * accepted=true 且 client_msg_id 匹配时，r2v 才回显；否则丢弃对应 pending。
 */
export interface LiveUpdateConfigAckPayload {
  client_msg_id?: string;
  accepted?: boolean;
  config_change_id?: string | number;
  error_code?: string;
  error_msg?: string;
}

export const getLiveConfigRateLimitRetrySeconds = (retryAfterMs?: number): number =>
  Math.ceil(Math.max(0, retryAfterMs ?? 0) / 1000);

export interface WsSignal {
  conn_id?: string;
  payload?: {
    /** 握手回执：success=false 且 error_code=NOT_READY 时需延时重发 CONN_INIT 轮询。 */
    conn_init_ack?: { success?: boolean; error_code?: string; error_msg?: string };
    /** 服务端强制挂断时下发；hangup_reason 非空则作为退出提示文案（对齐 iOS）。 */
    hangup?: { hangup_reason?: string };
    /** 客户端定期上报当前 RTC 媒体状态，供 connect 链路日志排障使用。 */
    media_status?: LiveMediaStatusPayload;
    /** 用户文本输入回执（text_input_ack=14）：accepted 时把对应 pending 气泡翻 sent。 */
    text_input_ack?: LiveTextInputAckPayload;
    /** 服务端错误信令（error=15），带 msg_id 时用于收口对应 pending 用户消息。 */
    error?: LiveErrorPayload;
    live_config_rate_limit_ack?: LiveConfigRateLimitAckPayload;
    /** 更新直播配置回执（update_live_config_ack=18）。 */
    update_live_config_ack?: LiveUpdateConfigAckPayload;
  };
  type?: number;
}

export const SIGNAL_TYPE_CONN_INIT = 1;
export const SIGNAL_TYPE_CONN_INIT_ACK = 2;
export const SIGNAL_TYPE_CALL_HANGUP = 5;
export const SIGNAL_TYPE_FORCE_HANGUP = 6;
export const SIGNAL_TYPE_MEDIA_STATUS = 11;
export const SIGNAL_TYPE_TIMEOUT_HANGUP = 10;
/** 用户文本输入（client→server，独立 type）。 */
export const SIGNAL_TYPE_TEXT_INPUT = 12;
/** 用户文本输入回执（server→client）：accepted 时把对应 pending 用户气泡翻 sent。 */
export const SIGNAL_TYPE_TEXT_INPUT_ACK = 14;
/** 服务端错误信令（server→client）：带 msg_id 时收口对应 pending 用户消息为 failed。 */
export const SIGNAL_TYPE_ERROR = 15;
/** 切换实时编辑场景（client→server）。 */
/**
 * 更新直播配置（client→server）。
 *
 * 注意：16 号在这次协议更新之前并不存在，前端曾自行占用它发 change_live_scene。
 * 现在 16 被正式定义为 update_live_config，语义与旧的私有格式不同，不要混用。
 */
export const SIGNAL_TYPE_UPDATE_LIVE_CONFIG = 16;
export const SIGNAL_TYPE_LIVE_CONFIG_RATE_LIMIT_ACK = 17;
/** 更新直播配置回执（server→client）。 */
export const SIGNAL_TYPE_UPDATE_LIVE_CONFIG_ACK = 18;

export type LiveCallMediaState =
  | 'connecting'
  | 'waiting_network'
  | 'reconnecting'
  | 'normal'
  | 'only_audio'
  | 'only_video'
  | 'video_stuck'
  | 'no_media'
  | 'autoplay_blocked';

export type LiveTrackStatus = 'no_track' | 'track_only' | 'receiving_packets' | 'flowing' | 'stalled' | 'rendered';

export interface LiveMediaStatusPayload {
  call_media_state: LiveCallMediaState;
  audio_status: LiveTrackStatus;
  video_status: LiveTrackStatus;
  remote_uid?: string;
  has_audio_track: boolean;
  has_video_track: boolean;
  is_call_ready: boolean;
  is_recovering: boolean;
  is_waiting_for_network: boolean;
  autoplay_blocked: boolean;
  audio_bytes_delta_3s: number;
  audio_packets_delta_3s: number;
  video_bytes_delta_3s: number;
  video_packets_delta_3s: number;
  video_frames_received_delta_3s: number;
  video_frames_decoded_delta_3s: number;
  video_frames_rendered_delta_3s: number;
  video_frames_rendered?: number;
  video_decode_fps?: number;
  video_frame_height?: number;
  video_frame_width?: number;
  subscribe_rtt_ms?: number;
  subscribe_loss?: number;
  subscribe_quality?: number;
  sampled_at_ms: number;
}

export const CONN_INIT_ERROR_NOT_READY = 'NOT_READY';

/** 收到 CONN_INIT_ACK 返回 NOT_READY 后，重发 CONN_INIT 的轮询间隔（毫秒）。 */
export const CONN_INIT_RETRY_INTERVAL_MS = 2_000;

/** 服务端下发的 error_code / hangup_reason → 前端 exit reason（语义相同则映射到已有 reason）。 */
const SERVER_CODE_TO_EXIT_REASON: Record<string, LiveExitReason> = {
  ended: 'ended',
  realtime_model_fail: 'realtime_model_fail',
  internal_fail: 'internal_fail',
  audit_rejected: 'audit_rejected',
  waiting_timeout: 'timeout_hangup',
  prepared_timeout: 'prepared_timeout',
  duration_expired: 'duration_limit',
  user_hangup: 'user_hangup',
  app_close_conn: 'app_close_conn',
  prepared_sip_disconnected: 'prepared_sip_disconnected',
  sip_reconnect_timeout: 'sip_reconnect_timeout',
  client_reconnect_timeout: 'connection_lost',
};

/** 仅触发重连、不作为最终退出态展示的错误码。 */
export const LIVE_RECONNECT_ONLY_REASONS = new Set<LiveExitReason>(['app_close_conn']);

/** 将服务端 error_code / hangup_reason 映射为退出 reason；未识别时返回 null。 */
export const resolveLiveExitReasonFromServerCode = (code: string | undefined): LiveExitReason | null => {
  const normalized = code?.trim().toLowerCase();

  if (!normalized) {
    return null;
  }

  return SERVER_CODE_TO_EXIT_REASON[normalized] ?? null;
};

export const isLiveReconnectOnlyReason = (reason: LiveExitReason): boolean => {
  return LIVE_RECONNECT_ONLY_REASONS.has(reason);
};

const LIVE_VIDEO_PUSH_UID_HINT = 'live-video-push';

export const normalizeError = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }

  return typeof error === 'string' ? error : JSON.stringify(error);
};

interface BuildWsUrlOptions {
  accessToken?: string | null;
  origin?: string;
}

/**
 * 拼直播 WebSocket 握手地址。
 *
 * `accessToken` 必须由调用方显式传入，不在这里读环境里的 token：曾经默认值写成
 * `getMiniappLiveAccessToken()`，让这个底层工具反向依赖了小程序这个上层场景，
 * 而且 token 的有无会改变 origin 的选择（见下），等于小程序的存在会影响非小程序
 * 的连接路径判定。注入点收敛到 LiveWsSession（见 resolveLiveWsAccessToken）。
 *
 * 带 token 时必须走同源：浏览器 WebSocket 不能自定义 Authorization 握手头，
 * 只能把凭证放在 query 上，由 Next 同源代理提升成 upstream 请求头并从 URL 摘掉。
 * 直连 API origin 的话这个提升就没人做了。
 */
export const buildWsUrl = (liveId: string, { accessToken, origin = API_ORIGIN }: BuildWsUrlOptions = {}): string => {
  const browserOrigin =
    accessToken && typeof window !== 'undefined' && window.location.origin ? window.location.origin : origin;
  const normalizedOrigin = browserOrigin.trim();

  if (!normalizedOrigin) {
    throw new Error('NEXT_PUBLIC_API_ORIGIN is missing');
  }

  const url = new URL(normalizedOrigin);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.pathname = '/live/ws/live/connect';
  const params = new URLSearchParams({ live_id: liveId });
  if (accessToken && getRequestPlatform() === WECHAT_MINIPROGRAM_PLATFORM) {
    params.set('x-platform', WECHAT_MINIPROGRAM_PLATFORM);
  }
  if (accessToken) {
    // Browser WebSocket cannot set a custom Authorization handshake header.
    // The same-origin Next proxy promotes this value to the upstream header and
    // removes it from the upstream URL before forwarding the handshake.
    params.set('authorization', `Bearer ${accessToken}`);
  }
  url.search = params.toString();

  return url.toString();
};

export const parseRtcAuthInfo = (rtc: LiveClaimRtcInfo): AliRtcAuthInfo | null => {
  const timestamp = typeof rtc.timestamp === 'string' ? Number(rtc.timestamp) : rtc.timestamp;

  if (!rtc.app_id || !rtc.channel_id || !rtc.user_id || !rtc.token || !Number.isFinite(timestamp)) {
    return null;
  }

  return {
    appId: rtc.app_id,
    channelId: rtc.channel_id,
    userId: rtc.user_id,
    timestamp: timestamp as number,
    token: rtc.token,
    nonce: rtc.nonce,
    sessionId: rtc.session_id,
  };
};

export const pickDisplayBinding = (bindings: RemoteVideoBinding[]): RemoteVideoBinding | null => {
  const explicitVideoPush = bindings.find((item) => item.uid.includes(LIVE_VIDEO_PUSH_UID_HINT));
  if (explicitVideoPush) {
    return explicitVideoPush;
  }

  const preferredCamera = bindings.find((item) => item.videoTrack === ALI_RTC_VIDEO_TRACK_CAMERA);
  if (preferredCamera) {
    return preferredCamera;
  }

  const preferredScreen = bindings.find((item) => item.videoTrack === ALI_RTC_VIDEO_TRACK_SCREEN);
  if (preferredScreen) {
    return preferredScreen;
  }

  const preferredBoth = bindings.find((item) => item.videoTrack === ALI_RTC_VIDEO_TRACK_BOTH);
  return preferredBoth ?? bindings[0] ?? null;
};

export const formatCallDuration = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export const getAvatarInitial = (name: string): string => {
  const source = name.trim();

  return source ? source.slice(0, 1).toUpperCase() : 'A';
};

export const getLiveAvatarPreviewImage = (avatar: LiveAvatar | null | undefined): string => {
  if (!avatar) {
    return '';
  }

  return (
    avatar.image_uri?.trim() || avatar.profile_photos?.[0]?.uri?.trim() || avatar.profile_photo_uris?.[0]?.trim() || ''
  );
};

/** 进入"收尾倒计时"展示的剩余秒数阈值（剩余 <= 60s 时大号 MM:SS + Ending Soon）。 */
export const ENDING_COUNTDOWN_THRESHOLD_SECONDS = 60;

/** 进入"最后倒计时"展示的剩余秒数阈值（剩余 <= 10s 时仅展示超大号秒数 + Ending Soon）。 */
export const FINAL_COUNTDOWN_THRESHOLD_SECONDS = 10;
